import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";
import {
  fetchVisitors,
  createVisitor,
  updateVisitor,
  updateVisitorStatus,
  deleteVisitor,
  markVisitorCheckedIn,
  markVisitorCheckedOut,
  VisitorQueryParams,
  VisitorListResponse,
  CreateVisitorPayload,
  UpdateVisitorPayload,
  VisitorStatus,
} from "../../services/visitorService";

/**
 * Query hook for visitor log registry and KPIs (Admin & SuperAdmin)
 */
export const useVisitorsQuery = (params?: VisitorQueryParams) => {
  return useQuery<VisitorListResponse>({
    queryKey: queryKeys.visitors.list(params),
    queryFn: ({ signal }) => fetchVisitors(params, signal),
  });
};

/**
 * Mutation hook to log a new visitor check-in
 */
export const useCreateVisitorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateVisitorPayload) => createVisitor(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.visitors.all });
    },
  });
};

/**
 * Mutation hook to transition visitor status (e.g., checkout, cancel)
 */
export const useUpdateVisitorStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, notes }: { id: number; status: VisitorStatus; notes?: string }) =>
      updateVisitorStatus(id, status, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.visitors.all });
    },
  });
};

/**
 * Mutation hook to update visitor record details
 */
export const useUpdateVisitorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateVisitorPayload }) =>
      updateVisitor(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.visitors.all });
    },
  });
};

/**
 * Mutation hook to delete visitor record
 */
export const useDeleteVisitorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteVisitor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.visitors.all });
    },
  });
};

/**
 * Mutation hook to check in a visitor
 */
export const useCheckInVisitorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => markVisitorCheckedIn(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.visitors.all });
    },
  });
};

/**
 * Mutation hook to check out a visitor
 */
export const useCheckOutVisitorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => markVisitorCheckedOut(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.visitors.all });
    },
  });
};

